import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from 'recharts';
import { Typography, TypographyOwnProps } from '@mui/material';
import { LegacyRef, useCallback } from 'react';

import { PieChartData } from '../../types';
import { getColor } from '../../utils/getColor';
import { useChartStyles } from './ChartStyles';
import { getPercentFromDecimalRatio } from '../../utils/getPercentFromDecimalRatio';

export interface ChartProps {
  className?: string;
  amount: string;
  amountTitle?: string;
  data: PieChartData[];
  hasOtherValuesSection?: boolean;
  amountClassName?: string;
  dataTableTypographyVariant?: TypographyOwnProps['variant'];
}

const OFFSET_X = 16;
const OFFSET_Y = 5;

interface PieLabelProps {
  cx: number;
  cy: number;
  midAngle: number;
  innerRadius: number;
  outerRadius: number;
  percent: number;
  index: number;
  value: number;
  x: number;
  y: number;
  ref: LegacyRef<SVGTextElement>;
}

export const Chart = ({
  amount,
  amountTitle = 'Total requests',
  className,
  data,
  hasOtherValuesSection,
  amountClassName,
  dataTableTypographyVariant,
}: ChartProps) => {
  const { classes, cx } = useChartStyles();

  const renderLabel = useCallback((label: PieLabelProps) => {
    const isFullValue = label.value === 1;
    const isSmallValue = label.value < 0.01;

    if (isSmallValue || isFullValue) {
      return null;
    }

    return (
      <text
        x={label.x - OFFSET_X}
        y={label.y + OFFSET_Y}
        fontSize={11}
        fill="grey"
        ref={label.ref}
      >
        {getPercentFromDecimalRatio(label.value)}
      </text>
    );
  }, []);

  return (
    <div className={cx(classes.root, className)}>
      <ResponsiveContainer height="100%" width="100%">
        <PieChart className={classes.chart}>
          <Pie
            cx="50%"
            cy="50%"
            data={data}
            dataKey="value"
            innerRadius="70%"
            labelLine={false}
            outerRadius="100%"
            label={renderLabel}
          >
            {data.map((_, index) => (
              <Cell
                key={`cell-${index}`}
                fill={getColor(index, hasOtherValuesSection)}
              />
            ))}
            <Tooltip />
          </Pie>
        </PieChart>
      </ResponsiveContainer>
      <div className={classes.amountWrapper}>
        <Typography
          className={cx(classes.amount, amountClassName)}
          variant="subtitle2"
        >
          {amount}
        </Typography>
        <Typography variant={dataTableTypographyVariant} color="textSecondary">
          {amountTitle}
        </Typography>
      </div>
    </div>
  );
};
